import { AxiosResponse } from "axios";
import { TFunction } from "i18next";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Toast } from "primereact/toast";
import { Tooltip } from 'primereact/tooltip';
import React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { SeverityEnum } from "../../shared/enum/SeverityEnum";
import { FormService } from "../../form/services/FormService";

import Editor from "@monaco-editor/react";
import * as monaco from 'monaco-editor/esm/vs/editor/editor.api';

import { AdminHtmlsService } from "../services/AdminHTMLService";
import { HtmlDTO } from "../dto/HtmlDTO";

import CodeSidebarHTML from "./components/HTML/CodeSidebarHTML";
import QuestionSelectorHTML from "./components/HTML/QuestionSelectorHTML";

import "./CodeEditor.scss";
import { Checkbox } from "primereact/checkbox";
import { Toolbar } from "primereact/toolbar";
import { InputText } from "primereact/inputtext";
import {Dialog} from "primereact/dialog";

interface Props extends WithTranslation {
  onDirtyChange: any;
}

interface States {
  displayConfirmation: boolean;
  displayRenameHtml: boolean;
  displayNewScriptConfirmation: boolean;
  displayHtmlInfo: boolean;
  editorText: string;
  editorHTMLName: string;
  actualHTMLName: string;
  isRenameRequest: boolean;
  refreshScriptsState: boolean;
  editorRef: React.MutableRefObject<monaco.editor.IStandaloneCodeEditor | null>;
  isActive: boolean;
  deleteHtml: boolean;
  downloadHtml: boolean;
  displayLeaveConfirmation: boolean;
  isDirty: boolean;
  isLoading: boolean,
  nextScript: string | null;
}


class CodeEditorHTML extends React.Component<Props, States> {


  private toast: Toast | null;
  private t: TFunction;
  private formService: FormService;

  private readonly htmlService: AdminHtmlsService;
  private readonly htmlDefaultScript: string;

  private leftContents: any;
  private rightContents: any;


  constructor(props: Props) {
    super(props);
    this.t = this.props.t;
    this.toast = null;
    this.formService = new FormService();

    // Languages
    this.htmlService = new AdminHtmlsService();
    this.htmlDefaultScript = `
      <!-- Variables to use : -->
    
      <!--
        {{question.QUESTION_NAME}}                              // Question's title
        {{value.QUESTION_NAME}}                                 // Answer's question
        {{requirement.title.REQUIREMENT_VALUE}}                 // Requirement's title
        {{requirement.answer.REQUIREMENT_VALUE}}                // Requirement's value
    
        // Submission properties:
        {{requirement.answer.submissionProps.submission_id}}    // Submission ID
        {{requirement.answer.submissionProps.userLastname}}     // User's last name
        {{requirement.answer.submissionProps.userFirstname}}    // User's first name
        {{requirement.answer.submissionProps.userEmail}}        // User's email
        {{requirement.answer.submissionProps.timestamp}}        // Timestamp
    
        {{translation.PRISMIC_KEY}}                             // Prismic's key
      -->
    `;

    this.state = {
      displayConfirmation: false,
      displayHtmlInfo: false,
      displayRenameHtml: false,
      editorText: "",
      editorHTMLName: "",
      actualHTMLName: "",
      isRenameRequest: false,
      displayNewScriptConfirmation: false,
      refreshScriptsState: false,
      editorRef: React.createRef<monaco.editor.IStandaloneCodeEditor | null>(),
      isActive: false,
      deleteHtml: false,
      downloadHtml: false,
      displayLeaveConfirmation: false,
      isDirty: false,
      isLoading: true,
      nextScript: null,
    };

    this.showHtmlInfoModal = this.showHtmlInfoModal.bind(this);
    this.hideHtmlInfoModal = this.hideHtmlInfoModal.bind(this);
    this.handleEditorChange = this.handleEditorChange.bind(this);
    this.resetEditorText = this.resetEditorText.bind(this);
    this.handleOneUpload = this.handleOneUpload.bind(this);
    this.handleEditorDidMount = this.handleEditorDidMount.bind(this);

  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.isDirty !== this.state.isDirty) {
      this.props.onDirtyChange(this.state.isDirty);
    }
  }

  componentDidMount() {
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  }

  handleBeforeUnload = (e) => {
    if (this.state.isDirty) {
      e.preventDefault();
      e.returnValue = 'Are you sure you want to leave this page? Unsaved changes will be lost.';
    }
  }

  requestNavigation = (nextScriptName) => {
    if (this.state.isDirty) {
      this.setState({
        displayLeaveConfirmation: true,
        nextScript: nextScriptName
      });
    } else {
      this.refreshScript(nextScriptName);
    }
  }

  confirmLeaveWithoutSaving = () => {
    this.setState({ displayLeaveConfirmation: false }, () => {
      if (this.state.nextScript) {
        this.refreshScript(this.state.nextScript);
      }
    });
  }

  cancelLeave = () => {
    this.setState({ displayLeaveConfirmation: false });
  }

  refreshScript = (scriptToView) => {
    this.setState(
        {
          editorHTMLName: scriptToView,
          isDirty: false,
          nextScript: null,
          isLoading: true
        }
    );

    this.getHtmlToViewText(scriptToView);

  };

  getHtmlToViewText(str: string) {
    if (!str || !str.includes(".html")) {
      return;
    }

    this.htmlService
      .getOne(str)
      .then((response: AxiosResponse<string>) => {
        this.setState({
          editorText: response.data,
          editorHTMLName: str,
          actualHTMLName: str,
          isLoading: false
        });

      });

    this.htmlService.getOneIsActive(str).then((res: any) => {
      if (res) {
        this.setState({
          isActive: res.data
        });
      }
    });
  }

  /**
   * On upload for one script
   */
  handleOneUpload() {
    let actualName = this.state.actualHTMLName;
    let isRenameRequest = this.state.isRenameRequest
    let newName = this.state.editorHTMLName;

    if (!newName) {
      this.toast?.show({
        severity: SeverityEnum.ERROR,
        detail: `Please enter a script name !`,
      });
      return;
    }

    let extensionPart = newName.substring(newName.lastIndexOf('.'));

    const allowedTypes = ['.html'];

    // = Validate the types
    if (!allowedTypes.includes(extensionPart)) {
      newName += ".html";
    }

    let dto: HtmlDTO;

    if (isRenameRequest) {
      dto = new HtmlDTO(actualName, newName, this.state.editorText, isRenameRequest);
      this.setState({isRenameRequest: false})
    }
    else {
      dto = new HtmlDTO(actualName, newName, this.state.editorText);
    }

    this.htmlService
      .uploadOne(dto)
      .then(() => {
        this.toast?.show({
          severity: SeverityEnum.SUCCESS,
          detail: this.formService.t(this.t, "ui_admin_html_upload_one_toast_success", false),
        });
        //this.resetEditorText();
        this.setState({ refreshScriptsState: true, isDirty: false });

      })
      .catch((err: any) => {
        const message = err?.response?.data?.message;
        this.toast?.show({
          severity: SeverityEnum.ERROR,
          detail: `${this.formService.t(this.t, "ui_admin_html_upload_one_toast_error", false)} : ${message}`,
        });
      });
  }


  resetEditorText() {
    this.setState({
      editorText: this.htmlDefaultScript,
      displayNewScriptConfirmation: false,
      editorHTMLName: "",
      actualHTMLName: ""
    });
  }


  /* 
    Actions handeling
   */
  handleEditorChange(value, event) {
    if (value !== this.state.editorText) {
      if (!this.state.isDirty) {
        this.setState({ isDirty: true });
      }
    }

    this.setState({ editorText: value });

  }

  /* 
    Validate methods
  */
  validationWindowForNewScript() {
    return (
      <div>
        <Button
          label="No"
          icon="pi pi-times"
          onClick={() => this.setState({ displayNewScriptConfirmation: false })}
          className="p-button-text"
        />
        <Button
          label="Yes"
          icon="pi pi-check"
          onClick={() => this.resetEditorText()}
          autoFocus
        />
      </div>
    );
  }


  handleDeletedScript = () => {
    this.resetEditorText();
  };


  handleEditorDidMount(editor, monaco) {

    this.state.editorRef.current = editor;

  }

  selectQuestion = (questionSelected) => {
    this.state.editorRef.current?.trigger('keyboard', 'type'
      , {
        text:
          '{{question.' + questionSelected.name + '}}\n\n'
      });
  };

  selectQuestionValue = (questionValueSelected) => {
    this.state.editorRef.current?.trigger('keyboard', 'type'
      , {
        text:
          '{{value.' + questionValueSelected.name + '}}\n\n'
      });
  };


  selectRequirement = (requirementSelected) => {
    this.state.editorRef.current?.trigger('keyboard', 'type'
      , {
        text:
          '{{requirement.title.' + requirementSelected.requirement + '}}\n\n'
      });
  };

  selectRequirementAnswer = (requirementAnswerSelected) => {
    this.state.editorRef.current?.trigger('keyboard', 'type'
      , {
        text:
          '{{requirement.answer.' + requirementAnswerSelected.requirement + '}}\n\n'
      });
  };

  selectPrismicKey = (prismicKeySelected) => {
    this.state.editorRef.current?.trigger('keyboard', 'type'
      , {
        text:
          '{{translation.' + prismicKeySelected + '}}\n'
      });
  };

  selectImagePrismicKey = (prismicKeySelected) => {
    this.state.editorRef.current?.trigger('keyboard', 'type'
      , {
        text: '<img width="200" src="{{image.' + prismicKeySelected + '}}" />'
      });
  };

  renderLeavingPageDialog() {
    return (
        <div>
          <Button label="Yes" icon="pi pi-check" onClick={this.confirmLeaveWithoutSaving}/>
          <Button label="No" icon="pi pi-times" onClick={this.cancelLeave}/>
        </div>
    );
  }

  loadFirstScript = (fileName) => {
    this.setState({ isLoading: true }, () => {
      this.refreshScript(fileName);
    });
  };

  showHtmlInfoModal() {
    this.setState({ displayHtmlInfo: true });
  }

  hideHtmlInfoModal() {
    this.setState({ displayHtmlInfo: false });
  }

  copyToClipboard(text: string) {
    navigator.clipboard.writeText(text).then(() => {
      this.toast?.show({ severity: 'success', detail: `Copied: ${text}` });
    }).catch(() => {
      this.toast?.show({ severity: 'error', detail: `Failed to copy: ${text}` });
    });
  }

  renameWindowForScript() {
    return (
      <div>
        <Button
          label="No"
          icon="pi pi-times"
          onClick={() => this.setState({
            displayRenameHtml: false ,
            isRenameRequest: false,
            editorHTMLName: this.state.actualHTMLName
          })}
          style={{ backgroundColor: '#0087b7', borderColor: '#0087b7' }}
          className="p-button-danger"
        />
        <Button
          label="Yes"
          icon="pi pi-check"
          onClick={() => {
            this.setState({
              displayRenameHtml: false,
            })
            this.handleOneUpload();
          }}
          autoFocus
          className="p-button-danger"
          style={{ backgroundColor: '#e98b3a', borderColor: '#e98b3a' }}
        />
      </div>
    );
  }

  render() {
    const htmlVariables = [
      { text: '{{question.QUESTION_NAME}}', description: 'Question\'s title' },
      { text: '{{value.QUESTION_NAME}}', description: 'Answer\'s question' },
      { text: '{{requirement.title.REQUIREMENT_VALUE}}', description: 'Requirement\'s title' },
      { text: '{{requirement.answer.REQUIREMENT_VALUE}}', description: 'Requirement\'s value' },
      { text: '{{requirement.answer.submissionProps.submission_id}}', description: 'Submission ID' },
      { text: '{{requirement.answer.submissionProps.userLastname}}', description: 'User\'s last name' },
      { text: '{{requirement.answer.submissionProps.userFirstname}}', description: 'User\'s first name' },
      { text: '{{requirement.answer.submissionProps.userEmail}}', description: 'User\'s email' },
      { text: '{{requirement.answer.submissionProps.timestamp}}', description: 'Timestamp' },
      { text: '{{translation.PRISMIC_KEY}}', description: 'Prismic\'s key' },
    ];

    const htmlInfoContent = (
      <div style={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
          <tr style={{ borderBottom: '1px solid #ddd', fontWeight: 'bold', textAlign: 'center' }}>
            <th style={{ width: '30%', padding: '8px' }}>Variable</th>
            <th style={{ width: '70%', padding: '8px' }}>Description</th>
          </tr>
          </thead>
          <tbody>
          {htmlVariables.map((variable, index) => (
            <tr
              key={index}
              style={{ cursor: 'pointer', borderBottom: '1px solid #ddd' }}
              onClick={() => this.copyToClipboard(variable.text)}
            >
              <td style={{ padding: '8px', color: '#007ad9' }}>{variable.text}</td>
              <td style={{ padding: '8px', color: '#555' }}>{variable.description}</td>
            </tr>
          ))}
          </tbody>
        </table>
        <p style={{ marginTop: '1rem', fontStyle: 'italic', color: '#888' }}>
          Click on a variable to copy it to your clipboard.
        </p>
      </div>
    );

    this.leftContents = (
      <React.Fragment>
        <h2 className="editorTitle">HTML template editor</h2>
        <Button
          icon="pi pi-info-circle"
          onClick={this.showHtmlInfoModal}
          className="align-right"
        />
        <Dialog
          header="HTML Variables Information"
          visible={this.state.displayHtmlInfo}
          style={{ width: '50vw' }}
          onHide={this.hideHtmlInfoModal}
          dismissableMask
        >
          {htmlInfoContent}
        </Dialog>
        <div className="toolbarCenterRequirement">
          <div className="">
            <InputText
              value={this.state.editorHTMLName}
              onChange={(e) => this.setState({ editorHTMLName: e.currentTarget.value })}

              placeholder="Template name"
              style={{ width: '15rem' }}
              readOnly
            />
            <Button className="p-button-text"
                    id="isTitleEditable"
                    icon="pi pi-pencil"
                    onClick={() => this.setState({
                      displayRenameHtml: true,
                      isRenameRequest: true
                    })}
            />
            <div>
              <Dialog
                header="Rename HTML file : "
                visible={this.state.displayRenameHtml}
                modal
                style={{ width: '400px' }}
                footer={this.renameWindowForScript()}
                onHide={() => this.setState({ displayRenameHtml: false })}
              >
                <div className="confirmation-content">
                  <label style={{ width:"100px" }}>New name : </label>
                  <InputText
                  value={this.state.editorHTMLName}
                  onChange={(e) => {
                    this.setState({ editorHTMLName: e.currentTarget.value })}
                  }
                  placeholder="Template name"
                  style={{ width: '15rem' }}
                  />
                </div>
              </Dialog>
            </div>

          <Tooltip target="#isTitleEditable" position="bottom">
            <p>
              The title is read-only by default. Click this button to switch it to edit mode and make it editable.
            </p>
          </Tooltip>
        </div>

        <div id="isActiveCheckbox">
          <div>
            <label id="actif_template_html_tooltip">Actif: </label>
            <Checkbox checked={this.state.isActive}
                      onChange={e => {
                        if (!this.state.editorHTMLName) {
                          this.toast?.show({
                            severity: SeverityEnum.ERROR,
                              detail: `Please select one template !`,
                            });
                            return;
                          }
                          this.htmlService.activeOne(this.state.editorHTMLName, e.checked).then(res => {
                            if (res.status === 201) {
                              this.toast?.show({
                                severity: SeverityEnum.SUCCESS,
                                detail: `Le changement a bien été effectué !`,
                              });
                              this.setState({ isActive: e.checked, refreshScriptsState: true });
                            }
                          });
                        }}/>
              <Tooltip target="#actif_template_html_tooltip" position="bottom">
                <p>
                  If you tick "Active", this template will be used to generate the results page at the end of the test.
                </p>
              </Tooltip>
            </div>
          </div>
        </div>
      </React.Fragment>
    );
    this.rightContents = (
      <div className="action">
        <Button
          type="button"
          tooltip="Download HTML template"
          icon="pi pi-download"
          style={{ backgroundColor: '#0087b7', borderColor: '#0087b7' }}
          className="p-button-danger"
          tooltipOptions={{ position: 'left' }}
          onClick={() => this.setState({
            downloadHtml: true,
          }, () => {
            this.setState({
              downloadHtml: false
            });
          })}
        />
        <Button
          tooltip="Save HTML template"
          icon="pi pi-save"
          className="p-button-danger"
          style={{ backgroundColor: '#e98b3a', borderColor: '#e98b3a' }}
          tooltipOptions={{ position: 'left' }}
          onClick={this.handleOneUpload }
        />
        <Button
          tooltip="Delete HTML template"
          tooltipOptions={{ position: 'left' }}
          type="button"
          icon="pi pi-trash"
          className="p-button-danger"
          onClick={() => this.setState({
            deleteHtml: true,
          }, () => {
            this.setState({
              deleteHtml: false
            });
          })}
        />
      </div>
    );

    return (
      <div className="editor-script">
        <div className="sideBarHTML">
          <CodeSidebarHTML
            requestNavigation={this.requestNavigation}
            scriptToViewCallback={this.refreshScript}
            reloadScripts={this.state.refreshScriptsState}
            resetRefreshScriptsState={() => this.setState({ refreshScriptsState: false })}
            deletedScript={this.handleDeletedScript}
            actionDeleteHtml={this.state.deleteHtml}
            actionDownloadHtml={this.state.downloadHtml}
            loadFirstScript={this.loadFirstScript}
          />
          <Dialog
              header="Confirmation"
              visible={this.state.displayLeaveConfirmation}
              modal
              style={{ width: "350px" }}
              footer={this.renderLeavingPageDialog()}
              onHide={() => this.setState({ displayLeaveConfirmation: false })}
          >
            <div className="confirmation-content">
              <i
                  className="pi pi-exclamation-triangle p-mr-3"
                  style={{ fontSize: "2rem" }}
              />
              <span>Are you sure you want to leave this page? Unsaved changes will be lost.</span>
            </div>
          </Dialog>
        </div>
        <Toolbar left={this.leftContents} right={this.rightContents}/>
        <div className="workPlace">
          <Card /*title={"Code editor HTML" this.formService.t(this.t, "ui_admin_editor_js_title", false) }*/>
            <Toast ref={(el) => (this.toast = el)}/>

            <div className="p-grid" id="scriptEditorHTML">
              <div className="p-col-12-jc-center" id="editorAreaHTML">
                {!this.state.isLoading && (
                  <Editor
                    height="100%"
                    defaultLanguage="html"
                    defaultValue={this.htmlDefaultScript}
                    value={this.state.editorText}
                    onChange={this.handleEditorChange}
                    onMount={this.handleEditorDidMount}
                  />
                )}
              </div>

              <div className="p-col-12 p-d-flex p-jc-center" id="questionSelectorHTML">
                <QuestionSelectorHTML
                  questionSelected={this.selectQuestion}
                  questionValueSelected={this.selectQuestionValue}
                  requirementSelected={this.selectRequirement}
                  requirementAnswerSelected={this.selectRequirementAnswer}
                  prismicKeySelected={this.selectPrismicKey}
                  imagePrismicKeySelected={this.selectImagePrismicKey}
                />
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

}

export default withTranslation()(CodeEditorHTML);
